import React, { useState, useMemo } from 'react';
import { Map, Users, Clock, Compass, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card } from '../ui';
import { Input } from '../ui';

export function CanvassingRouteOptimizer() {
  const [totalDoors, setTotalDoors] = useState<number>(5000);
  const [volunteers, setVolunteers] = useState<number>(15);
  const [shiftHours, setShiftHours] = useState<number>(3);
  const [doorsPerHour, setDoorsPerHour] = useState<number>(20);
  const [daysAvailable, setDaysAvailable] = useState<number>(14);

  const model = useMemo(() => {
    // Capacity calculations
    const doorsPerShiftPerVolunteer = doorsPerHour * shiftHours;
    const totalCapacityPerShift = doorsPerShiftPerVolunteer * volunteers;
    
    // How many total shifts required to hit the universe?
    const requiredShifts = Math.ceil(totalDoors / totalCapacityPerShift);
    
    // Total capacity over the available days (assuming 1 shift per day)
    const maxCapacity = totalCapacityPerShift * daysAvailable;
    
    const isFeasible = maxCapacity >= totalDoors;
    const capacityPercentage = Math.min(100, (maxCapacity / totalDoors) * 100);

    return {
      doorsPerShiftPerVolunteer,
      totalCapacityPerShift,
      requiredShifts,
      maxCapacity,
      isFeasible,
      capacityPercentage
    };
  }, [totalDoors, volunteers, shiftHours, doorsPerHour, daysAvailable]);

  return (
    <div className="max-w-5xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-sky-600 to-indigo-800 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <Compass className="w-6 h-6 mr-3 text-sky-300" />
            Canvassing Turf & Capacity Optimizer
          </h2>
          <p className="text-sky-100 max-w-xl">
            Ensure your field team can actually hit their universe. Model your door-knocking capacity against your available volunteer pool to identify shortfalls before the final GOTV push.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        
        {/* Controls */}
        <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
                <Map className="w-4 h-4 mr-2 text-sky-500" /> Total Targeted Doors (Universe)
              </label>
              <Input 
                type="number" 
                value={totalDoors} 
                onChange={(e) => setTotalDoors(Number(e.target.value))}
                className="text-lg font-bold"
              />
            </div>
            
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
                <Users className="w-4 h-4 mr-2 text-sky-500" /> Volunteers Available
              </label>
              <Input 
                type="number" 
                value={volunteers} 
                onChange={(e) => setVolunteers(Number(e.target.value))}
              />
            </div>
            
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
                <Clock className="w-4 h-4 mr-2 text-sky-500" /> Hours Per Shift
              </label>
              <Input 
                type="number" 
                value={shiftHours} 
                onChange={(e) => setShiftHours(Number(e.target.value))}
              />
            </div>

            <div className="col-span-2 pt-4 border-t border-slate-100 dark:border-slate-800">
              <label className="text-sm font-semibold text-slate-500 uppercase flex justify-between mb-2">
                <span>Doors Knocked Per Hour (Pace)</span>
                <span className="font-bold text-slate-900 dark:text-white">{doorsPerHour} doors/hr</span>
              </label>
              <input 
                type="range" 
                min="5" 
                max="45" 
                value={doorsPerHour} 
                onChange={(e) => setDoorsPerHour(Number(e.target.value))}
                className="w-full accent-sky-600"
              />
              <p className="text-xs text-slate-400 mt-2">Dense urban: ~30/hr. Suburban: ~20/hr. Rural: ~10/hr.</p>
            </div>

            <div className="col-span-2 pt-4 border-t border-slate-100 dark:border-slate-800">
              <label className="text-sm font-semibold text-slate-500 uppercase flex justify-between mb-2">
                <span>Days Until Election / Deadline</span>
                <span className="font-bold text-slate-900 dark:text-white">{daysAvailable} Days</span>
              </label>
              <input 
                type="range" 
                min="1" 
                max="60" 
                value={daysAvailable} 
                onChange={(e) => setDaysAvailable(Number(e.target.value))}
                className="w-full accent-sky-600"
              />
            </div>
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          <Card className={`p-6 border-slate-200 dark:border-slate-800 shadow-xl text-white ${model.isFeasible ? 'bg-sky-900' : 'bg-red-900'}`}>
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-sm font-bold text-sky-300 uppercase tracking-wider">Campaign Feasibility</h3>
              {model.isFeasible ? <CheckCircle2 className="w-8 h-8 text-sky-400" /> : <AlertTriangle className="w-8 h-8 text-red-400" />}
            </div>
            
            <div className="text-4xl font-black tracking-tight mb-2">
              {model.isFeasible ? 'On Track' : 'Capacity Shortfall'}
            </div>
            <p className="text-sky-200 text-sm">
              Your field team can hit <strong>{model.maxCapacity.toLocaleString()}</strong> out of {totalDoors.toLocaleString()} doors in the remaining time.
            </p>

            {/* Progress Bar */}
            <div className="mt-4 h-3 bg-black/20 rounded-full overflow-hidden border border-white/10">
              <div 
                className={`h-full ${model.isFeasible ? 'bg-sky-400' : 'bg-red-400'}`} 
                style={{ width: `${model.capacityPercentage}%` }}
              />
            </div>
            {!model.isFeasible && (
              <div className="mt-2 text-xs text-red-200 font-bold uppercase tracking-wider text-right">
                {model.capacityPercentage.toFixed(1)}% Coverage
              </div>
            )}
          </Card>

          <Card className="p-0 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 overflow-hidden">
            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 border-b border-slate-100 dark:border-slate-800">
              <h3 className="font-bold">Turf Math Breakdown</h3>
            </div>
            
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              <div className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Individual Turf Size</div>
                  <div className="text-sm text-slate-500">Doors per volunteer per {shiftHours}hr shift</div>
                </div>
                <div className="text-2xl font-bold text-sky-600 dark:text-sky-400">
                  {model.doorsPerShiftPerVolunteer}
                </div>
              </div>
              
              <div className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Single Shift Capacity</div>
                  <div className="text-sm text-slate-500">With {volunteers} active volunteers</div>
                </div>
                <div className="text-2xl font-bold">
                  {model.totalCapacityPerShift}
                </div>
              </div>

              <div className="p-4 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Total Shifts Required</div>
                  <div className="text-sm text-slate-500">To cover the entire universe</div>
                </div>
                <div className="text-2xl font-black text-slate-900 dark:text-white">
                  {model.requiredShifts}
                </div>
              </div>
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}
